// components/AgreementPicker.js
// Used inside the invite modal when sending an introducer invite.
// Auto-selects if 1 template. Shows picker if 2+. Warns if 0.

import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || 'https://brokerconnect.co.uk/api';

/**
 * Props:
 *   token         — auth token
 *   value         — currently selected template id (null = none)
 *   onChange      — (templateId | null) => void
 *   onCanInvite   — (bool) => void  — tells parent whether invite can proceed
 */
export default function AgreementPicker({ token, value, onChange, onCanInvite }) {
  const [templates, setTemplates] = useState([]);
  const [loading,   setLoading]   = useState(true);

  const load = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/agreement-templates/for-invite`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setTemplates(list);

      if (list.length === 0) {
        onChange(null);
        if (onCanInvite) onCanInvite(false);
      } else if (list.length === 1) {
        // Auto-select
        onChange(list[0].id);
        if (onCanInvite) onCanInvite(true);
      } else {
        // Keep current selection or default to first
        if (!value) onChange(list[0].id);
        if (onCanInvite) onCanInvite(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ fontSize:13, color:'#94a3b8', padding:'12px 0' }}>Loading agreements…</div>
  );

  // ── No templates ──────────────────────────────────────────────────────────
  if (templates.length === 0) {
    return (
      <div style={{
        background:'#fffbeb', border:'1px solid #fcd34d',
        borderRadius:10, padding:16,
      }}>
        <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
          <span style={{ fontSize:20, flexShrink:0 }}>⚠️</span>
          <div>
            <p style={{ margin:'0 0 6px', fontWeight:700, fontSize:13, color:'#92400e' }}>
              No approved introducer agreement live yet
            </p>
            <p style={{ margin:0, fontSize:12, color:'#78350f', lineHeight:1.6 }}>
              You do not currently have any approved introducer agreements live in the system —
              please email <strong>support@brokerconnect.co.uk</strong> with your network agreement
              and we can then work with your network to approve an automated workflow.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Single template — auto-selected confirmation ──────────────────────────
  if (templates.length === 1) {
    const t = templates[0];
    return (
      <div style={{
        background:'#f0fdf4', border:'1px solid #bbf7d0',
        borderRadius:10, padding:14, display:'flex', gap:10, alignItems:'center',
      }}>
        <span style={{ fontSize:20 }}>📄</span>
        <div>
          <p style={{ margin:'0 0 2px', fontWeight:600, fontSize:13, color:'#166534' }}>
            {t.display_name || t.original_name}
          </p>
          <p style={{ margin:0, fontSize:12, color:'#16a34a' }}>
            v{t.version} · {t.field_count} fields · This agreement will be sent with the invite
          </p>
        </div>
      </div>
    );
  }

  // ── Multiple templates — radio picker ─────────────────────────────────────
  return (
    <div>
      <p style={{ margin:'0 0 10px', fontSize:12, fontWeight:600, color:'#4b5a6e',
        textTransform:'uppercase', letterSpacing:'0.05em' }}>
        Select agreement for this invite
      </p>
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {templates.map(t => {
          const selected = value === t.id;
          return (
            <div
              key={t.id}
              onClick={() => { onChange(t.id); if (onCanInvite) onCanInvite(true); }}
              style={{
                display:'flex', alignItems:'center', gap:12,
                padding:'12px 14px', borderRadius:10, cursor:'pointer',
                border: `2px solid ${selected ? '#10b981' : '#e2e8f0'}`,
                background: selected ? '#f0fdf4' : '#fff',
                transition:'all 0.15s',
              }}
            >
              {/* Radio circle */}
              <div style={{
                width:18, height:18, borderRadius:'50%', flexShrink:0,
                border: `2px solid ${selected ? '#10b981' : '#cbd5e0'}`,
                background: selected ? '#10b981' : '#fff',
                display:'flex', alignItems:'center', justifyContent:'center',
              }}>
                {selected && <div style={{ width:7, height:7, borderRadius:'50%', background:'#fff' }} />}
              </div>

              {/* Template info */}
              <div style={{ flex:1 }}>
                <p style={{ margin:'0 0 2px', fontWeight:600, fontSize:13,
                  color: selected ? '#166534' : '#2d3748' }}>
                  {t.display_name || t.original_name}
                </p>
                <p style={{ margin:0, fontSize:11, color:'#94a3b8' }}>
                  v{t.version} · {t.field_count} fields
                </p>
              </div>

              {selected && (
                <span style={{ fontSize:16, color:'#10b981' }}>✓</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
